import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import SurveyResponseDetails from './SurveyResponseDetails';
import {
  XMarkIcon,
  ChartBarIcon,
  ChartPieIcon,
  UserGroupIcon,
  CheckCircleIcon,
  DocumentChartBarIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

interface SurveyStatistics {
  frage_id: number;
  frage_text: string;
  frage_typ: 'single' | 'multiple' | 'freitext';
  antworten: {
    [key: string]: {
      count: number;
      percentage: number;
      text?: string;
    };
  };
  freitext_antworten?: string[];
  total_antworten: number;
}

interface ModuleStatistics {
  modul_id: number;
  modul_titel: string;
  modul_typ: string;
  total_antworten: number;
  umfrage_statistik?: SurveyStatistics[];
}

interface ResponseStatisticsProps {
  elternbriefId: number;
  isOpen: boolean;
  onClose: () => void;
}

const ResponseStatistics: React.FC<ResponseStatisticsProps> = ({
  elternbriefId,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<ModuleStatistics[]>([]);
  const [generalStats, setGeneralStats] = useState<any>(null);
  const [showSurveyDetails, setShowSurveyDetails] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [selectedModuleTitel, setSelectedModuleTitel] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadStatistics();
    }
  }, [isOpen, elternbriefId]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      
      // Load general statistics
      const statsResponse = await api.get(`/massenmail/${elternbriefId}/statistics`);
      if (statsResponse.data.success) {
        setGeneralStats(statsResponse.data.data);
      }
      
      // Load detailed response statistics
      const responseStatsResponse = await api.get(`/massenmail/${elternbriefId}/response-statistics`);
      if (responseStatsResponse.data.success) {
        setStatistics(responseStatsResponse.data.data);
      }
    } catch (error: any) {
      console.error('Error loading statistics:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const renderSurveyStatistics = (survey: SurveyStatistics) => {
    return (
      <div key={survey.frage_id} className="bg-gray-50 p-4 rounded-lg">
        <div className="font-medium text-gray-900 mb-3">
          {survey.frage_text}
        </div>
        
        <div className="text-sm text-gray-600 mb-3">
          Antworttyp: {survey.frage_typ === 'single' ? 'Einfachauswahl' : 
                       survey.frage_typ === 'multiple' ? 'Mehrfachauswahl' : 
                       'Freitext'} • {survey.total_antworten} Antworten
        </div>

        {survey.frage_typ !== 'freitext' ? (
          <div className="space-y-3">
            {Object.entries(survey.antworten).map(([key, data]) => (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{data.text || key}</span>
                  <span className="font-medium text-gray-900">
                    {data.count} ({data.percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${data.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {survey.freitext_antworten && survey.freitext_antworten.map((antwort, idx) => (
              <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{antwort}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Antwort-Statistiken
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Laden...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* General Statistics */}
                {generalStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <UserGroupIcon className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {generalStats.total}
                      </div>
                      <div className="text-sm text-gray-600">Empfänger</div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <CheckCircleIcon className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-600">
                        {generalStats.beantwortet}
                      </div>
                      <div className="text-sm text-gray-600">Beantwortet</div>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <ChartPieIcon className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-blue-600">
                        {generalStats.geoeffnet}
                      </div>
                      <div className="text-sm text-gray-600">Geöffnet</div>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg text-center">
                      <DocumentChartBarIcon className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-yellow-600">
                        {generalStats.ausstehend}
                      </div>
                      <div className="text-sm text-gray-600">Ausstehend</div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <ChartBarIcon className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-purple-600">
                        {generalStats.ruecklaufquote}%
                      </div>
                      <div className="text-sm text-gray-600">Rücklauf</div>
                    </div>
                  </div>
                )}

                {/* Response Rate Chart */}
                {generalStats && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Rücklaufquote</h4>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-700">Beantwortet</span>
                            <span className="text-sm font-medium text-gray-900">
                              {generalStats.beantwortet} ({((generalStats.beantwortet / generalStats.total) * 100).toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-green-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${(generalStats.beantwortet / generalStats.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-700">Geöffnet (nicht beantwortet)</span>
                            <span className="text-sm font-medium text-gray-900">
                              {generalStats.geoeffnet} ({((generalStats.geoeffnet / generalStats.total) * 100).toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${(generalStats.geoeffnet / generalStats.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-700">Ausstehend</span>
                            <span className="text-sm font-medium text-gray-900">
                              {generalStats.ausstehend} ({((generalStats.ausstehend / generalStats.total) * 100).toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-yellow-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${(generalStats.ausstehend / generalStats.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Module Statistics */}
                {statistics.length > 0 ? (
                  <div className="space-y-6">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <ChartBarIcon className="h-5 w-5" />
                      Detaillierte Modul-Statistiken
                    </h4>
                    
                    {statistics.map((module) => (
                      <div key={module.modul_id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {module.modul_titel}
                            </h5>
                            <div className="text-sm text-gray-600">
                              {module.modul_typ === 'lesebestaetigung' ? 'Lesebestätigung' :
                               module.modul_typ === 'umfrage' ? 'Umfrage' :
                               module.modul_typ === 'texteingabe' ? 'Texteingabe' :
                               module.modul_typ}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-blue-600">
                                {module.total_antworten}
                              </div>
                              <div className="text-sm text-gray-600">Antworten</div>
                            </div>
                            
                            {module.modul_typ === 'umfrage' && module.total_antworten > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedModuleId(module.modul_id);
                                  setSelectedModuleTitel(module.modul_titel);
                                  setShowSurveyDetails(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                              >
                                <TableCellsIcon className="h-4 w-4" />
                                Detailansicht
                              </button>
                            )}
                          </div>
                        </div>

                        {module.modul_typ === 'umfrage' && module.umfrage_statistik && (
                          <div className="space-y-4">
                            {module.umfrage_statistik.map((survey) => renderSurveyStatistics(survey))}
                          </div>
                        )}

                        {module.modul_typ === 'lesebestaetigung' && (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700">
                              <CheckCircleIcon className="h-5 w-5" />
                              <span className="font-medium">
                                {module.total_antworten} Empfänger haben die Lesebestätigung gegeben
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Noch keine Antworten vorhanden
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Schließen
            </button>
          </div>

          {/* Survey Details Modal */}
          {selectedModuleId && (
            <SurveyResponseDetails
              elternbriefId={elternbriefId}
              modulId={selectedModuleId}
              modulTitel={selectedModuleTitel}
              isOpen={showSurveyDetails}
              onClose={() => {
                setShowSurveyDetails(false);
                setSelectedModuleId(null);
                setSelectedModuleTitel('');
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponseStatistics;
